import type { ReactNode } from "react";
import { Button, LinkButton, QuietButton, SecondaryButton } from "../ui/button";
import { Screen } from "../ui/screen";
import { HouseRulesViewer } from "./viewer";
import type { HouseRules } from "../../lib/api";

type Props = {
  rules: HouseRules;
  handle: string | null;
  /** A short, self-clearing acknowledgement of the last thing that happened. */
  note: string | null;
  error: string | null;
  /** The surfaced proposals section (M15, FR-8.3), sat above the document. */
  proposals?: ReactNode;
  onEditMyself: () => void;
  onTellNob: () => void;
  onHistory: () => void;
  /** Present when there is a door screen to go back to. */
  onBack?: (() => void) | undefined;
  /** What the back link is called — the door in onboarding, the recap from home (M16). */
  backLabel?: string;
};

/**
 * The rules home — where a returning recipient lands (FR-2.1). The document
 * first, because it is the product; the three ways to change it underneath it.
 *
 * THREE ways, not five. The Inbox and the money buttons that used to sit in
 * this row are gone (owner, 2026-08-25): this document lives inside the
 * settings panel now, and the inbox is the list standing behind that panel
 * while the plan is a panel of its own. Both were links out of a room somebody
 * opened to read their rules — navigation dressed as an action, in the row
 * where every other button changes the document.
 */
export function RulesHome({
  rules,
  handle,
  note,
  error,
  proposals,
  onEditMyself,
  onTellNob,
  onHistory,
  onBack,
  backLabel = "Your door",
}: Props) {
  return (
    <Screen wide>
      {onBack && (
        <p className="mb-6">
          <LinkButton type="button" onClick={onBack}>
            {backLabel}
          </LinkButton>
        </p>
      )}

      <div className="flex items-baseline justify-between gap-4">
        <p className="text-[13px] uppercase tracking-[0.18em] text-faint">
          House Rules{handle ? ` — @${handle}` : ""}
        </p>
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-fainter">
          Version {rules.version}
        </span>
      </div>

      <p className="mt-2 max-w-lg text-[13px] leading-snug text-muted">
        The whole of your doorman&rsquo;s instructions. Nothing else configures it. Change them
        yourself, or say what you want changed.
      </p>

      <div className="mt-4 min-h-[1.25rem]" aria-live="polite">
        {note && <p className="nob-ack font-mono text-[11px] text-accent-ink/80">{note}</p>}
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </div>

      {proposals}

      <div className="mt-3 rounded-xl border border-border bg-surface/30 p-4">
        <HouseRulesViewer markdown={rules.markdown} />
      </div>

      {/* NAMED, so the CTA that means "change my rules" can land on it. The
          three controls are the bottom of a long block and "Personalize your
          house rules" opens this page pointing at them (`SettingsFocus`); a
          scroll target chosen by position would move the first time somebody
          added a paragraph above it. */}
      <div data-rules-controls className="mt-6 flex flex-wrap items-center gap-2">
        <Button type="button" onClick={onTellNob}>
          Tell Nob
        </Button>
        <SecondaryButton type="button" onClick={onEditMyself}>
          Edit myself
        </SecondaryButton>
        <QuietButton type="button" onClick={onHistory}>
          History
        </QuietButton>
      </div>

      <p className="mt-4 text-xs text-fainter">
        Every change is shown as a diff before it is saved, and every version is kept.
      </p>
    </Screen>
  );
}
